import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { KoremarginLogo } from '../ui/KoremarginLogo'

interface FormData {
  password: string
  confirmPassword: string
}

export function UpdatePasswordPage() {
  const { updatePassword } = useAuth()
  const navigate = useNavigate()
  const [serverError, setServerError] = useState<string | null>(null)
  const [invalidToken, setInvalidToken] = useState(false)

  // Supabase reindirizza con il token nell'hash URL (#access_token=...)
  // onAuthStateChange in AuthContext lo intercetta automaticamente e aggiorna la sessione
  useEffect(() => {
    const hash = window.location.hash
    if (!hash.includes('access_token') && !hash.includes('type=recovery')) {
      setInvalidToken(true)
    }
  }, [])

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>()

  const password = watch('password')

  async function onSubmit(data: FormData) {
    setServerError(null)
    const err = await updatePassword(data.password)
    if (err) {
      setServerError(err)
    } else {
      navigate('/login', { state: { passwordUpdated: true }, replace: true })
    }
  }

  if (invalidToken) {
    return (
      <div className="min-h-screen bg-[--km-bg] flex items-center justify-center p-4">
        <div className="w-full max-w-sm text-center">
          <div className="flex flex-col items-center mb-8 gap-3">
            <KoremarginLogo variant="full" />
          </div>
          <div className="bg-[--km-surface] border border-[--km-border] rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-[--km-text-primary] mb-2">Link non valido</h2>
            <p className="text-sm text-[--km-text-secondary]">
              Il link di recupero è scaduto o non valido. Richiedi un nuovo link.
            </p>
          </div>
          <p className="mt-4 text-sm text-[--km-text-secondary]">
            <Link to="/reset-password" className="text-[--km-primary] hover:underline font-medium">
              Richiedi nuovo link
            </Link>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[--km-bg] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8 gap-3">
          <h1 className="sr-only">Koremargin</h1>
          <KoremarginLogo variant="full" />
          <p className="text-sm text-[--km-text-secondary]">
            Business Intelligence Suite
          </p>
        </div>

        <div className="bg-[--km-surface] border border-[--km-border] rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-[--km-text-primary] mb-1">Nuova password</h2>
          <p className="text-sm text-[--km-text-secondary] mb-5">
            Scegli una nuova password per il tuo account.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-[--km-text-secondary] mb-1"
              >
                Nuova password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                placeholder="••••••••"
                className="w-full px-3 py-2 text-sm border border-[--km-border] rounded-lg bg-[--km-bg] text-[--km-text-primary] placeholder-[--km-text-secondary] focus:outline-none focus:ring-2 focus:ring-[--km-primary] focus:border-transparent"
                {...register('password', {
                  required: 'Password obbligatoria.',
                  minLength: { value: 6, message: 'La password deve contenere almeno 6 caratteri.' },
                })}
              />
              {errors.password && (
                <p role="alert" className="mt-1 text-xs text-[--km-error]">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-[--km-text-secondary] mb-1"
              >
                Conferma nuova password
              </label>
              <input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                placeholder="••••••••"
                className="w-full px-3 py-2 text-sm border border-[--km-border] rounded-lg bg-[--km-bg] text-[--km-text-primary] placeholder-[--km-text-secondary] focus:outline-none focus:ring-2 focus:ring-[--km-primary] focus:border-transparent"
                {...register('confirmPassword', {
                  required: 'Conferma la password.',
                  validate: (v) => v === password || 'Le password non coincidono.',
                })}
              />
              {errors.confirmPassword && (
                <p role="alert" className="mt-1 text-xs text-[--km-error]">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            {serverError && (
              <div role="alert" className="rounded-lg bg-red-50 border border-[--km-error] px-3 py-2">
                <p className="text-sm text-[--km-error]">{serverError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-2 px-4 text-sm font-medium text-white bg-[--km-primary] rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {isSubmitting ? 'Salvataggio in corso…' : 'Imposta nuova password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
